import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { commentApi, Comment } from '@/lib/commentApi';
import { QUICK_REACTIONS } from '@/lib/emojis';
import { SmilePlus, MessageSquareText, ChevronDown, ChevronRight, FileText, Image as ImageIcon, Download } from 'lucide-react';
import { sanitizeHtml } from '@fyos/components';
import ConfirmDialog from './ConfirmDialog';
import CommentEditor from './CommentEditor';

interface CommentSectionProps {
  workspaceId: string;
  itemId: string;
  showHeader?: boolean;
  layout?: 'inline' | 'panel';
  onCommentCountChange?: (count: number) => void;
}

export default function CommentSection({ workspaceId, itemId, showHeader = true, layout = 'inline', onCommentCountChange }: CommentSectionProps) {
  const { user } = useAuthStore();
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(true);
  const [replyTo, setReplyTo] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deleteCommentId, setDeleteCommentId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [reactionPickerId, setReactionPickerId] = useState<string | null>(null);
  const [expandedReplies, setExpandedReplies] = useState<Set<string>>(new Set());
  const [, setTimeUpdate] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showTopShadow, setShowTopShadow] = useState(false);
  const [showBottomShadow, setShowBottomShadow] = useState(false);

  const updateScrollShadows = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setShowTopShadow(el.scrollTop > 8);
    setShowBottomShadow(el.scrollHeight - el.scrollTop - el.clientHeight > 8);
  }, []);

  useEffect(() => {
    fetchComments();
  }, [workspaceId, itemId]);

  useEffect(() => {
    // Recalculate scroll shadows after comments change
    requestAnimationFrame(updateScrollShadows);
  }, [comments, loading, updateScrollShadows]);

  useEffect(() => {
    // Update time display every minute
    const interval = setInterval(() => {
      setTimeUpdate(prev => prev + 1);
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  const fetchComments = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const data = await commentApi.getAll(workspaceId, itemId);
      setComments(data);
    } catch (err) {
      console.error('Failed to fetch comments:', err);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const handleSubmitComment = async (content: string, contentHtml: string, attachmentIds: string[]) => {
    if (!content.trim() && attachmentIds.length === 0) return;

    try {
      setSubmitting(true);
      const comment = await commentApi.create(workspaceId, itemId, {
        content,
        content_html: contentHtml,
        attachment_ids: attachmentIds.length > 0 ? attachmentIds : undefined,
      });
      const normalizedComment = { ...comment, replies: comment.replies || [], is_deleted: comment.is_deleted ?? false, reactions: comment.reactions || [], attachments: comment.attachments || [] };
      const newComments = [...comments, normalizedComment];
      setComments(newComments);
      
      // Notify parent of comment count change
      const totalCount = newComments.reduce((acc, c) => acc + 1 + (c.replies?.length || 0), 0);
      onCommentCountChange?.(totalCount);
    } catch (err) {
      console.error('Failed to create comment:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitReply = async (parentId: string, content: string, contentHtml: string, attachmentIds: string[]) => {
    if (!content.trim() && attachmentIds.length === 0) return;

    try {
      setSubmitting(true);
      const reply = await commentApi.create(workspaceId, itemId, {
        content,
        content_html: contentHtml,
        parent_id: parentId,
        attachment_ids: attachmentIds.length > 0 ? attachmentIds : undefined,
      });
      const normalizedReply = { ...reply, replies: reply.replies || [], is_deleted: reply.is_deleted ?? false, reactions: reply.reactions || [], attachments: reply.attachments || [] };
      
      const newComments = comments.map(c => {
        if (c.id === parentId) {
          return {
            ...c,
            replies: [...(c.replies || []), normalizedReply],
          };
        }
        return c;
      });
      setComments(newComments);
      
      setReplyTo(null);
      
      // Ensure replies are expanded so the new reply is visible
      setExpandedReplies(prev => new Set(prev).add(parentId));
      
      // Notify parent of comment count change
      const totalCount = newComments.reduce((acc, c) => acc + 1 + (c.replies?.length || 0), 0);
      onCommentCountChange?.(totalCount);
    } catch (err) {
      console.error('Failed to create reply:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateComment = async (commentId: string, content: string, contentHtml: string) => {
    if (!content.trim()) return;

    try {
      await commentApi.update(workspaceId, itemId, commentId, {
        content,
        content_html: contentHtml,
      });
      
      fetchComments();
      setEditingId(null);
    } catch (err) {
      console.error('Failed to update comment:', err);
    }
  };

  const handleDeleteComment = (commentId: string) => {
    setDeleteCommentId(commentId);
  };

  const confirmDeleteComment = async () => {
    if (!deleteCommentId) return;
    try {
      await commentApi.delete(workspaceId, itemId, deleteCommentId);
      
      // Check if this is a top-level comment or a reply
      const isTopLevel = comments.some(c => c.id === deleteCommentId);
      
      let newComments: Comment[];
      if (isTopLevel) {
        const comment = comments.find(c => c.id === deleteCommentId);
        if (comment?.replies && comment.replies.length > 0) {
          // Has replies — mark as deleted placeholder, keep replies visible
          newComments = comments.map(c =>
            c.id === deleteCommentId ? { ...c, is_deleted: true, content: '' } : c
          );
        } else {
          // No replies — remove entirely
          newComments = comments.filter(c => c.id !== deleteCommentId);
        }
      } else {
        // It's a reply — remove from parent's replies array
        newComments = comments.map(c => ({
          ...c,
          replies: c.replies?.filter(r => r.id !== deleteCommentId),
        }));
        // If a deleted parent now has zero replies, remove it entirely
        newComments = newComments.filter(c => !c.is_deleted || (c.replies && c.replies.length > 0));
      }
      
      setComments(newComments);
      
      // Notify parent of comment count change (exclude deleted placeholders from count)
      const totalCount = newComments.reduce((acc, c) => {
        const parentCount = c.is_deleted ? 0 : 1;
        return acc + parentCount + (c.replies?.length || 0);
      }, 0);
      onCommentCountChange?.(totalCount);
    } catch (err) {
      console.error('Failed to delete comment:', err);
    } finally {
      setDeleteCommentId(null);
    }
  };

  const handleReaction = async (commentId: string, emoji: string) => {
    try {
      await commentApi.addReaction(workspaceId, itemId, commentId, emoji);
      fetchComments(true);
    } catch (err) {
      console.error('Failed to add reaction:', err);
    }
  };

  const getTimeAgo = (dateString: string) => {
    // Server returns UTC timestamps without timezone suffix (e.g. "2026-02-21 00:05:00").
    // Append 'Z' so JS parses them as UTC instead of local time.
    const normalized = dateString.includes('Z') || dateString.includes('+') || dateString.includes('T')
      ? dateString
      : dateString.replace(' ', 'T') + 'Z';
    const date = new Date(normalized);
    const now = new Date();
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (seconds < 10) return 'just now';
    if (seconds < 60) return `${seconds}s ago`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    if (seconds < 2592000) return `${Math.floor(seconds / 86400)}d ago`;
    return date.toLocaleDateString();
  };

  const toggleReplies = (commentId: string) => {
    setExpandedReplies(prev => {
      const next = new Set(prev);
      if (next.has(commentId)) {
        next.delete(commentId);
      } else {
        next.add(commentId);
      }
      return next;
    });
  };

  const renderBubble = (comment: Comment) => {
    const isEditing = editingId === comment.id;
    const isDeleted = !!comment.is_deleted;

    if (isDeleted) {
      return (
        <div className="bg-gray-100 dark:bg-gray-700/30 rounded-lg p-3 border border-dashed border-gray-300 dark:border-gray-600">
          <p className="text-sm text-gray-400 dark:text-gray-500 italic">[comment deleted]</p>
        </div>
      );
    }

    return (
      <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 break-words">
          <div className="flex items-center justify-between mb-1">
            <span className="text-sm font-medium text-gray-900 dark:text-white">
              {comment.user?.name || 'Unknown User'}
            </span>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {getTimeAgo(comment.created_at)}
              {!!comment.is_edited && ' (edited)'}
            </span>
          </div>
          
          {isEditing ? (
            <CommentEditor
              workspaceId={workspaceId}
              onSubmit={(content, contentHtml) => {
                handleUpdateComment(comment.id, content, contentHtml);
              }}
              placeholder="Edit your comment..."
              submitLabel="Save"
              compact
              autoFocus
              initialContent={comment.content_html || comment.content}
              onCancel={() => setEditingId(null)}
            />
          ) : (
            <>
              {comment.content_html ? (
                <div
                  className="text-sm text-gray-700 dark:text-gray-300 prose prose-sm dark:prose-invert max-w-none break-words [&_p]:my-1 [&_ul]:my-1 [&_ol]:my-1"
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(comment.content_html) }}
                />
              ) : (
                <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-words overflow-wrap-anywhere">{comment.content}</p>
              )}
              {comment.attachments && comment.attachments.length > 0 && (
                <div className="mt-2 space-y-1">
                  {comment.attachments.map(att => (
                    <a
                      key={att.id}
                      href={att.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`flex items-center gap-2 text-xs bg-gray-100 dark:bg-gray-600/50 rounded px-2 py-1.5 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors group ${att.path ? 's3-asset' : ''}`}
                      {...(att.path ? { 'data-s3-path': att.path, 'data-s3-expires': Date.now().toString() } : {})}
                    >
                      {att.is_image ? (
                        <ImageIcon size={14} className="text-blue-500 flex-shrink-0" />
                      ) : (
                        <FileText size={14} className="text-gray-400 flex-shrink-0" />
                      )}
                      <span className="truncate text-gray-600 dark:text-gray-300 flex-1">{att.original_name}</span>
                      <Download size={12} className="text-gray-400 opacity-0 group-hover:opacity-100 flex-shrink-0" />
                    </a>
                  ))}
                </div>
              )}
            </>
          )}
      </div>
    );
  };

  const renderActions = (comment: Comment, isReply = false) => {
    const isEditing = editingId === comment.id;
    const isOwner = comment.user_id === user?.id;
    const isDeleted = !!comment.is_deleted;
    if (isDeleted) return null;

    return (
      <div className="flex items-center space-x-4 mt-2">
          {!isReply && (
            <button
              onClick={() => {
                setReplyTo(comment.id);
                if (!expandedReplies.has(comment.id)) {
                  toggleReplies(comment.id);
                }
              }}
              className="text-xs text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
            >
              Reply
            </button>
          )}
          
          {isOwner && !isEditing && (
            <>
              <button
                onClick={() => {
                  setEditingId(comment.id);
                }}
                className="text-xs text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
              >
                Edit
              </button>
              <button
                onClick={() => handleDeleteComment(comment.id)}
                className="text-xs text-red-600 hover:text-red-900"
              >
                Delete
              </button>
            </>
          )}
          
          <div className="relative">
            <button
              type="button"
              onClick={() => setReactionPickerId(reactionPickerId === comment.id ? null : comment.id)}
              className="inline-flex items-center justify-center h-8 w-8 rounded-full border border-dashed border-gray-300 text-gray-400 hover:border-gray-400 hover:text-gray-500 dark:border-gray-600 dark:hover:border-gray-500 dark:hover:text-gray-300 transition-colors"
              title="Add reaction"
            >
              <SmilePlus className="h-3.5 w-3.5" />
            </button>
            
            {reactionPickerId === comment.id && (
              <div className="absolute left-0 top-full mt-1 z-10 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-2 min-w-[200px]">
                <div className="grid grid-cols-4 gap-1">
                  {QUICK_REACTIONS.map(emoji => {
                    const reaction = comment.reactions?.find(r => r.emoji === emoji);
                    const hasReacted = reaction?.user_ids.includes(user?.id || '');
                    
                    return (
                      <button
                        key={emoji}
                        onClick={() => {
                          handleReaction(comment.id, emoji);
                          setReactionPickerId(null);
                        }}
                        className={`px-2 py-1 text-xs rounded hover:bg-gray-200 dark:hover:bg-gray-600 ${
                          hasReacted ? 'bg-blue-100 dark:bg-blue-900/30' : 'bg-gray-100 dark:bg-gray-700'
                        }`}
                      >
                        {emoji} {reaction && reaction.count > 0 ? reaction.count : ''}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            
            {comment.reactions && comment.reactions.length > 0 && (
              <div className="flex items-center space-x-1 mt-2">
                {comment.reactions.map(reaction => (
                  <button
                    key={reaction.emoji}
                    onClick={() => handleReaction(comment.id, reaction.emoji)}
                    className={`px-2 py-1 text-xs rounded hover:bg-gray-200 dark:hover:bg-gray-600 ${
                      reaction.user_ids.includes(user?.id || '') ? 'bg-blue-100 dark:bg-blue-900/30' : 'bg-gray-100 dark:bg-gray-700'
                    }`}
                  >
                    {reaction.emoji} {reaction.count}
                  </button>
                ))}
              </div>
            )}
          </div>
      </div>
    );
  };

  const renderComment = (comment: Comment, isReply = false) => {
    const isDeleted = !!comment.is_deleted;
    const replyCount = comment.replies?.length || 0;
    const hasReplies = !isReply && replyCount > 0;
    const isExpanded = expandedReplies.has(comment.id);
    const showReplies = hasReplies && isExpanded;
    const showReplyForm = !isReply && replyTo === comment.id;

    const avatarClasses = isReply
      ? `w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium ${
          isDeleted ? 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400' : 'bg-blue-500 text-white'
        }`
      : `w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
          isDeleted ? 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400' : 'bg-blue-500 text-white'
        }`;
    const avatarLetter = isDeleted ? '?' : (comment.user?.name?.charAt(0).toUpperCase() || 'U');

    // Reply: avatar centered with bubble, actions below
    if (isReply) {
      return (
        <div key={comment.id}>
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0">
              <div data-reply-avatar="true" className={avatarClasses}>{avatarLetter}</div>
            </div>
            <div className="flex-1 min-w-0 overflow-hidden">
              {renderBubble(comment)}
            </div>
          </div>
          <div style={{ marginLeft: 28 + 12 }}>
            {renderActions(comment, true)}
          </div>
        </div>
      );
    }

    // Layout: avatar + bubble in one row (items-center), actions/toggle below.
    // Trunk line is absolutely positioned on the outer wrapper, from avatar bottom
    // down through actions/toggle and into the replies area.
    const CURVE_R = 8;
    const AVATAR_W = 32;
    const GAP = 12; // gap-3

    return (
      <div
        key={comment.id}
        className="mb-5 relative"
        ref={(wrapper) => {
          if (!wrapper || !showReplies) return;
          const trunk = wrapper.querySelector('[data-trunk]') as HTMLElement | null;
          const repliesContainer = wrapper.querySelector('[data-replies]') as HTMLElement | null;
          const avatar = wrapper.querySelector('[data-avatar]') as HTMLElement | null;
          if (!trunk || !repliesContainer || !avatar) return;

          const wrapperRect = wrapper.getBoundingClientRect();
          const avatarRect = avatar.getBoundingClientRect();
          const lastBranch = repliesContainer.querySelector('[data-reply-wrapper]:last-child') as HTMLElement | null;

          // Trunk starts at avatar bottom
          const trunkTop = avatarRect.bottom - wrapperRect.top;
          trunk.style.top = `${trunkTop}px`;

          if (lastBranch) {
            // Trunk ends where the last reply's curve begins (not at avatar center)
            const lastAvatar = lastBranch.querySelector('[data-reply-avatar]') as HTMLElement | null;
            if (lastAvatar) {
              const avatarCenterY = lastAvatar.getBoundingClientRect().top + 14 - wrapperRect.top;
              // Curve radius is 8px, so the vertical trunk should stop where the curve starts
              trunk.style.height = `${avatarCenterY - 8 - trunkTop}px`;
            }
          }
        }}
      >
        {/* Trunk line — absolutely positioned from avatar bottom */}
        {showReplies && (
          <div
            data-trunk="true"
            className="absolute bg-gray-300 dark:bg-gray-600 pointer-events-none"
            style={{ left: (AVATAR_W - 1.5) / 2, width: 1.5 }}
          />
        )}

        {/* Avatar + bubble row — items-center so avatar centers with bubble only */}
        <div className="flex items-center gap-3">
          <div className="flex-shrink-0" style={{ width: AVATAR_W }}>
            <div data-avatar="true" className={avatarClasses}>{avatarLetter}</div>
          </div>
          <div className="flex-1 min-w-0 overflow-hidden">
            {renderBubble(comment)}
          </div>
        </div>

        {/* Actions below bubble, aligned with bubble */}
        <div style={{ marginLeft: AVATAR_W + GAP }}>
          {renderActions(comment)}
          {hasReplies && (
            <button
              type="button"
              onClick={() => toggleReplies(comment.id)}
              className="flex items-center gap-1 mt-2 text-xs font-medium text-brand-600 hover:text-brand-700 dark:text-brand-400 dark:hover:text-brand-300"
            >
              {isExpanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
              {isExpanded ? 'Hide' : 'Show'} {replyCount} {replyCount === 1 ? 'reply' : 'replies'}
            </button>
          )}
        </div>

        {/* Replies: per-reply curved branches from the trunk */}
        {showReplies && (
          <div data-replies="true" style={{ marginLeft: (AVATAR_W - 1.5) / 2 }}>
            {comment.replies!.map((reply) => (
              <div
                key={reply.id}
                data-reply-wrapper="true"
                className="relative"
                style={{ paddingLeft: 28, paddingTop: 10 }}
                ref={(el) => {
                  if (!el) return;
                  const replyAvatar = el.querySelector('[data-reply-avatar]') as HTMLElement | null;
                  const branch = el.querySelector('[data-branch]') as HTMLElement | null;
                  if (!replyAvatar || !branch) return;
                  // Position branch so its horizontal line aligns with the reply avatar center
                  const avatarTop = replyAvatar.getBoundingClientRect().top - el.getBoundingClientRect().top;
                  const avatarCenterY = avatarTop + 14; // 28px avatar / 2
                  branch.style.top = `${avatarCenterY - 14}px`; // branch height is 14, bottom edge at avatarCenterY
                  branch.style.height = `${14}px`;
                }}
              >
                {/* Curved branch from trunk into reply — 8px gap before content */}
                <div
                  data-branch="true"
                  className="absolute border-gray-300 dark:border-gray-600 pointer-events-none"
                  style={{
                    left: 0,
                    width: 20,
                    borderBottomWidth: 1.5,
                    borderLeftWidth: 1.5,
                    borderBottomStyle: 'solid',
                    borderLeftStyle: 'solid',
                    borderBottomLeftRadius: CURVE_R,
                    borderTopColor: 'transparent',
                    borderRightColor: 'transparent',
                  }}
                />
                {renderComment(reply, true)}
              </div>
            ))}
          </div>
        )}

        {/* Reply form */}
        {showReplyForm && (
          <div className="mt-3" style={{ marginLeft: (AVATAR_W - 1.5) / 2, paddingLeft: 20 }}>
            <CommentEditor
              workspaceId={workspaceId}
              onSubmit={(content, contentHtml, attachmentIds) => {
                handleSubmitReply(comment.id, content, contentHtml, attachmentIds);
              }}
              placeholder="Write a reply..."
              submitLabel="Reply"
              compact
              autoFocus
              disabled={submitting}
              onCancel={() => setReplyTo(null)}
            />
          </div>
        )}
      </div>
    );
  };

  const commentForm = (
    <CommentEditor
      workspaceId={workspaceId}
      onSubmit={handleSubmitComment}
      placeholder="Add a comment..."
      submitLabel={submitting ? 'Posting...' : 'Post Comment'}
      compact={layout === 'panel'}
      disabled={submitting}
    />
  );

  const emptyState = (
    <div className="flex flex-col items-center justify-center py-12 text-gray-400 dark:text-gray-500">
      <MessageSquareText className="h-10 w-10 mb-3 opacity-40" />
      <p className="text-sm font-medium">No comments yet</p>
      <p className="text-xs mt-1">Be the first to share your thoughts.</p>
    </div>
  );

  const commentList = loading ? (
    <div className="text-sm text-center py-8 text-gray-500 dark:text-gray-400">Loading comments...</div>
  ) : comments.length === 0 ? (
    emptyState
  ) : (
    <div>
      {comments.map(comment => renderComment(comment))}
    </div>
  );

  if (layout === 'panel') {
    return (
      <div className="flex flex-col h-full">
        <div className="relative flex-1 min-h-0 overflow-hidden">
          <div
            ref={scrollRef}
            onScroll={updateScrollShadows}
            className="h-full overflow-y-auto"
          >
            {commentList}
          </div>
          <div
            className={`pointer-events-none absolute top-0 left-0 right-0 h-6 bg-gradient-to-b from-white dark:from-gray-800 to-transparent z-10 transition-opacity duration-200 ${showTopShadow ? 'opacity-100' : 'opacity-0'}`}
          />
          <div
            className={`pointer-events-none absolute bottom-0 left-0 right-0 h-6 bg-gradient-to-t from-white dark:from-gray-800 to-transparent z-10 transition-opacity duration-200 ${showBottomShadow ? 'opacity-100' : 'opacity-0'}`}
          />
        </div>
        <div className="flex-shrink-0 border-t border-gray-200 dark:border-gray-700 pt-3">
          {commentForm}
        </div>
        <ConfirmDialog
          open={!!deleteCommentId}
          onOpenChange={(open) => { if (!open) setDeleteCommentId(null); }}
          title="Delete Comment"
          description="Are you sure you want to delete this comment?"
          confirmLabel="Delete"
          variant="danger"
          onConfirm={confirmDeleteComment}
        />
      </div>
    );
  }

  return (
    <div className={showHeader ? 'mt-6' : ''}>
      {showHeader && (
        <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
          Comments ({comments.reduce((acc, c) => acc + (c.is_deleted ? 0 : 1) + (c.replies?.length || 0), 0)})
        </h3>
      )}
      
      <div className="mb-6">
        {commentForm}
      </div>
      
      {commentList}
      <ConfirmDialog
        open={!!deleteCommentId}
        onOpenChange={(open) => { if (!open) setDeleteCommentId(null); }}
        title="Delete Comment"
        description="Are you sure you want to delete this comment?"
        confirmLabel="Delete"
        variant="danger"
        onConfirm={confirmDeleteComment}
      />
    </div>
  );
}
